/**
 * 업무 프로젝트 자동화 — 노드 종류의 화면 쪽 목록(J7c1).
 *
 * - 🔴 NODE_TYPES 키는 J7a1 `backend/services/task_pm_automation.py` 의 TRIGGERS·CONDITIONS·ACTIONS 와
 *   글자·순서까지 같다(서버가 정본이다 — 이름을 바꾸면 저장이 «알 수 없는 노드» 로 막힌다).
 * - 팔레트 표시 순서 = NODE_TYPES 의 키 순서(트리거 6 → 조건 1 → 액션 4).
 * - 연결 규칙(canConnect)은 서버 검사기 ④·⑤ 와 같은 것을 미리 본다 — 막히는 선은 아예 그리지 않는다.
 */

export const GROUPS = [
  { value: 'trigger', label: '트리거', hint: '하나만 놓을 수 있습니다' },
  { value: 'condition', label: '조건' },
  { value: 'action', label: '액션' },
];

export const FIELD_LABELS = { status: '상태', assignee: '담당자', priority: '우선순위', type: '유형' };
export const FIELDS = ['status', 'assignee', 'priority', 'type'];

// 필드 바꾸기의 담당자 줄 방식 — 🔴 값은 J1 ASSIGNEE_MODES 와 같다. replace 는 저장할 때 mode 키를 싣지 않는다
// (키 없음 = replace · 옛 저장 모양 그대로) → 새 줄 기본 데이터에도 mode 를 넣지 않는다.
export const ASSIGNEE_MODES = [
  { value: 'replace', label: '바꾸기' },
  { value: 'add', label: '더하기' },
  { value: 'remove', label: '빼기' },
];

// input = 왼쪽 입력 연결점이 있나 · outputs = 오른쪽 출력 연결점(트리거 null 하나 · 조건 yes/no · 액션 없음)
// data() = 새로 놓을 때의 기본값(설정은 J7d 패널이 채운다).
export const NODE_TYPES = {
  trigger_field_change:       { group: 'trigger',   label: '필드가 바뀌면',          input: false, outputs: [null], data: () => ({ fields: [...FIELDS] }) },
  trigger_task_created:       { group: 'trigger',   label: '업무가 생기면',          input: false, outputs: [null], data: () => ({}) },
  trigger_task_deleted:       { group: 'trigger',   label: '업무가 지워지면',        input: false, outputs: [null], data: () => ({}) },
  trigger_task_archived:      { group: 'trigger',   label: '업무가 보관되면',        input: false, outputs: [null], data: () => ({}) },
  trigger_comment_added:      { group: 'trigger',   label: '댓글이 달리면',          input: false, outputs: [null], data: () => ({}) },
  trigger_checklist_complete: { group: 'trigger',   label: '체크리스트를 다 끝내면', input: false, outputs: [null], data: () => ({}) },
  condition_if:               { group: 'condition', label: '조건 분기',              input: true,  outputs: ['yes', 'no'], data: () => ({ logic: 'and', conditions: [] }) },
  action_set_field:           { group: 'action',    label: '필드 바꾸기',            input: true,  outputs: [], data: () => ({ fields: [] }) },
  action_create_task:         { group: 'action',    label: '업무 만들기',            input: true,  outputs: [], data: () => ({ title: '', status: null, assignee: null, type: null, priority: null, as_subtask: false }) },
  action_archive_task:        { group: 'action',    label: '업무 보관',              input: true,  outputs: [], data: () => ({}) },
  action_messenger_post:      { group: 'action',    label: '메신저 채널에 글',       input: true,  outputs: [], data: () => ({ channel_id: '', message: '' }) },
};

export const HANDLE_LABELS = { yes: '예', no: '아니오' };

export const NODE_W = 180;
export const NODE_H = 52;

const TITLE_MAX = 30;

// 🔴 `NODE_TYPES[type]` 만 보면 'constructor' 같은 이름이 Object 의 것을 집는다 — 자기 키만 인정한다.
export function nodeSpec(type) {
  return typeof type === 'string' && Object.prototype.hasOwnProperty.call(NODE_TYPES, type) ? NODE_TYPES[type] : null;
}

const fieldText = (list) => list.map((f) => FIELD_LABELS[f] || String(f)).join('·');

// 노드 몸 둘째 줄(12px · 한 줄 말줄임은 캔버스가 한다).
export function summarize(node) {
  const data = (node && node.data) || {};
  switch (node && node.type) {
    case 'trigger_field_change': {
      const fields = Array.isArray(data.fields) ? data.fields : [];
      return fields.length ? `살필 필드: ${fieldText(fields)}` : '살필 필드 없음';
    }
    case 'condition_if': {
      const count = Array.isArray(data.conditions) ? data.conditions.length : 0;
      if (!count) return '조건 없음';
      return `조건 ${count}개 · ${data.logic === 'or' ? '하나라도' : '모두'}`;
    }
    case 'action_set_field': {
      // 줄 모양은 서버와 같은 {field, value} 다.
      const rows = Array.isArray(data.fields) ? data.fields : [];
      if (!rows.length) return '바꿀 필드 없음';
      return fieldText(rows.map((row) => (row && typeof row === 'object' ? row.field : row)));
    }
    case 'action_create_task': {
      const title = typeof data.title === 'string' ? data.title.trim() : '';
      if (!title) return '제목 없음';
      return title.length > TITLE_MAX ? `${title.slice(0, TITLE_MAX)}…` : title;
    }
    case 'action_messenger_post':
      return data.channel_id ? '채널 지정됨' : '채널 없음';
    default:
      return '';
  }
}

// 서버 검사기와 같은 규칙: 양 끝이 있다 · 자기 자신 아님 · source 에 그 출력이 있다 · target 에 입력이 있다 ·
// target 에 들어오는 연결이 아직 없다(합류 금지) · 같은 연결이 없다 · target 에서 source 로 돌아오지 않는다(고리 금지).
export function canConnect(graph, sourceId, handle, targetId) {
  const nodes = Array.isArray(graph && graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph && graph.edges) ? graph.edges : [];
  const source = nodes.find((n) => n.id === sourceId);
  const target = nodes.find((n) => n.id === targetId);
  if (!source || !target || sourceId === targetId) return false;
  const sourceSpec = nodeSpec(source.type);
  const targetSpec = nodeSpec(target.type);
  if (!sourceSpec || !targetSpec) return false;
  const h = handle === undefined ? null : handle;
  if (!sourceSpec.outputs.includes(h)) return false;
  if (!targetSpec.input) return false;
  if (edges.some((e) => e.target === targetId)) return false;
  if (edges.some((e) => e.source === sourceId && (e.handle ?? null) === h && e.target === targetId)) return false;

  const seen = new Set([targetId]);
  const stack = [targetId];
  while (stack.length) {
    const current = stack.pop();
    if (current === sourceId) return false;
    edges.forEach((e) => {
      if (e.source === current && !seen.has(e.target)) {
        seen.add(e.target);
        stack.push(e.target);
      }
    });
  }
  return true;
}

// 'n'·'e' + (같은 접두어 뒤가 숫자인 id 중 최대 + 1) — 다른 모양의 id(서버가 준 것 등)는 건너뛴다.
export function nextId(list, prefix) {
  let max = 0;
  (Array.isArray(list) ? list : []).forEach((item) => {
    const id = item && item.id != null ? String(item.id) : '';
    if (!id.startsWith(prefix)) return;
    const rest = id.slice(prefix.length);
    if (!/^\d+$/.test(rest)) return;
    const n = Number(rest);
    if (n > max) max = n;
  });
  return `${prefix}${max + 1}`;
}
